import type { QuotaBlockInfo } from '#shared/usage/quotaBlock'

type Translate = (key: string, params?: Record<string, unknown>) => string

/**
 * The one line that explains a quota block to a user — shared by the web's Activity Center row
 * (`stores/documentsIndexing.ts`) and the Word add-in's document banner (MAR-948), so both say the
 * same thing (MAR-42, §10.7). `exceeds_plan` never processes on this plan, so it must not read as
 * "wait for the period to reset"; with no facts at all, the generic line — never the raw sentinel.
 *
 * Per seat (MAR-1046 AC17): a block another user's seat hit is not the viewer's limit, so they get a
 * neutral line saying they can re-run it, never the other user's numbers or plan.
 */
export function quotaBlockMessage(
  t: Translate,
  quota: Pick<QuotaBlockInfo, 'reason' | 'categoryLabel' | 'needed' | 'remaining' | 'limit' | 'userId'> | null | undefined,
  viewerId?: string | null,
): string {
  if (!quota) return t('indexing.quotaBlockedGeneric')
  if (!isOwnQuotaBlock(quota, viewerId)) return t('indexing.quotaBlockedOtherSeat')
  return quota.reason === 'exceeds_plan'
    ? t('indexing.quotaExceedsPlan', { category: quota.categoryLabel, needed: quota.needed, limit: quota.limit ?? 0 })
    : t('indexing.quotaExhausted', { category: quota.categoryLabel, needed: quota.needed, remaining: quota.remaining })
}

/**
 * Whether the viewer is the user whose seat hit `quota` — the only one shown the upgrade notice and
 * the red banner. A block that names no user (recorded before per-seat quota) counts as anyone's.
 */
export function isOwnQuotaBlock(quota: Pick<QuotaBlockInfo, 'userId'> | null | undefined, viewerId: string | null | undefined): boolean {
  return !quota?.userId || !viewerId || quota.userId === viewerId
}
